import { ClaimLineStatus } from '@rialtic/types'
import { useClaimLab } from '../stores/claimLab'
import type { RawInsight, RawInsights } from '../types'

const INSIGHT_TYPE = [
  'Claim Line Not Payable',
  'Claim Line Partially Payable',
  'Claim Not Payable',
  'Recode Claim Line',
]

const getRelevantInsights = (
  insights: RawInsights | undefined,
  selectedInsightOnly: boolean,
) => {
  const result =
    insights && selectedInsightOnly
      ? insights.filter((insight) => insight.is_recommendation)
      : insights

  return [...(result ?? [])]
}

export const getActiveClaimLine = (
  [firstTestInsight]: RawInsights = [],
  [firstExistingInsight]: RawInsights = [],
): number => {
  if (firstTestInsight) {
    return firstTestInsight.insight.insight.claimLineSequenceNum
  }

  if (firstExistingInsight) {
    return firstExistingInsight.insight.insight.claimLineSequenceNum
  }

  return 1
}

export const useHelpers = (
  userPermissions: ComputedRef<string[]> | undefined,
) => {
  const claimLab = useClaimLab()

  const isConsoleUser = computed(() =>
    userPermissions?.value && claimLab.workspaceId
      ? userPermissions.value.includes(
          `workspace:${claimLab.workspaceId}#${WORKSPACE_PERMISSION.readConnectorConfig}`,
        )
      : false,
  )

  const getUnifiedStatus = (insights: RawInsights | undefined) => {
    if (!insights) return ClaimLineStatus.NO_INSIGHTS

    const filteredInsights = getRelevantInsights(insights, !isConsoleUser.value)

    const recommendedInsight: RawInsight | undefined = filteredInsights.find(
      (insight) => insight.is_recommendation,
    )

    const isNonPayable =
      recommendedInsight &&
      INSIGHT_TYPE.includes(recommendedInsight.insight.insight.type)

    const hasExcluded = filteredInsights.some(
      (insight) => insight.excluded_by?.length,
    )

    if (
      filteredInsights.length &&
      filteredInsights.every(
        (insight) =>
          insight.insight.insight_mode.toLowerCase() ===
          ClaimLineStatus.OBSERVATION,
      )
    )
      return ClaimLineStatus.OBSERVATION

    if (isNonPayable) return ClaimLineStatus.NOT_PAYABLE

    if (!recommendedInsight && hasExcluded) return ClaimLineStatus.EXCLUDED

    return filteredInsights.length
      ? ClaimLineStatus.OBSERVATION
      : ClaimLineStatus.NO_INSIGHTS
  }

  const getExistingStatus = (
    existingInsights: RawInsights,
    isNewClaimLine = false,
  ): ClaimLineStatus => {
    if (isNewClaimLine) return ClaimLineStatus.NEW_LINE

    return getUnifiedStatus(existingInsights)
  }

  const getTestStatus = (
    existingInsights: RawInsights,
    testInsights: RawInsights | undefined = undefined,
    otherClaimSequence: number | undefined = undefined,
  ): ClaimLineStatus => {
    // Right now we only support 1 other claim, in the future this check may need to be updated to support multiple other claims
    if (otherClaimSequence !== undefined) {
      return claimLab.influencingClaimLines
        .get('OTHER-CLAIM-0')
        ?.has(otherClaimSequence)
        ? ClaimLineStatus.INFLUENCING
        : ClaimLineStatus.NOT_INFLUENCING
    }

    // test ran and had no insights and existing insights were not present or it was a new claim line
    if (
      (!existingInsights || !existingInsights.length) &&
      (!testInsights || !testInsights.length)
    )
      return ClaimLineStatus.NO_INSIGHTS

    // test ran and had no insights
    if (
      (existingInsights && !testInsights) ||
      (existingInsights && !testInsights?.length)
    )
      return ClaimLineStatus.RESOLVED

    return getUnifiedStatus(testInsights)
  }

  const getStatus = (
    existingInsights: RawInsights | undefined,
    testInsights?: RawInsights,
    hasTestResults = false,
  ): ClaimLineStatus => {
    const normalizedExisting: RawInsights = existingInsights ?? []

    if (hasTestResults) {
      return getTestStatus(normalizedExisting, testInsights)
    }

    return getExistingStatus(normalizedExisting)
  }

  return {
    isConsoleUser,
    getExistingStatus,
    getTestStatus,
    getStatus,
    getActiveClaimLine,
  }
}
